"use strict";
Object.defineProperty(exports, "__esModule", { value: true });
exports.consumerWebhookUrlInputSchema = exports.consumerWebhookUrlSchema = exports.consumerWebhookEventSchema = exports.consumerWebhookTestEventSchema = exports.consumerWebhookStateChangedEventSchema = exports.CONSUMER_WEBHOOK_URL_REJECTION_LABELS = exports.CONSUMER_WEBHOOK_URL_REJECTIONS = exports.CONSUMER_WEBHOOK_DELIVERY_ERROR_LABELS = exports.CONSUMER_WEBHOOK_DELIVERY_ERRORS = exports.CONSUMER_WEBHOOK_DELIVERY_STATUS_LABELS = exports.CONSUMER_WEBHOOK_DELIVERY_STATUSES = exports.CONSUMER_WEBHOOK_URL_MAX = exports.CONSUMER_WEBHOOK_SECRET_PREFIX = exports.CONSUMER_WEBHOOK_DELIVERIES_LIMIT = exports.CONSUMER_WEBHOOK_PREVIOUS_SECRET_HOURS = exports.CONSUMER_WEBHOOK_TIMEOUT_SECONDS = exports.CONSUMER_WEBHOOK_MAX_ATTEMPTS = exports.CONSUMER_WEBHOOK_RETRY_DELAYS_SECONDS = exports.CONSUMER_WEBHOOK_EVENT_TYPE_LABELS = exports.CONSUMER_WEBHOOK_EVENT_TYPES = void 0;
const zod_1 = require("zod");
const ticket_state_js_1 = require("./ticket-state.js");
/**
 * Notifica di cambio stato dei ticket verso i consumer (DEC-013 regole 6-10, story #74, #84): un
 * webhook facoltativo per consumer, firmato secondo Standard Webhooks, consegnato almeno una volta
 * con ritentativi per circa un giorno. Due superfici:
 *
 * - l'**evento** che EasyHelp invia al consumer (chiavi in italiano, come il resto del contratto
 *   verso i prodotti): stesso ambito e contenuto minimo della lettura dello stato (#73), più
 *   segnalante e stato precedente;
 * - l'**amministrazione** nel registro dei consumer (chiavi in inglese, come `Consumer`): attivare,
 *   modificare, disattivare, rigenerare il segreto, vedere le ultime consegne, inviare una prova.
 */
/** Tipi di evento: il cambio di stato di un ticket e la prova inviata dall'amministratore. */
exports.CONSUMER_WEBHOOK_EVENT_TYPES = ['ticket.stato_cambiato', 'webhook.prova'];
exports.CONSUMER_WEBHOOK_EVENT_TYPE_LABELS = {
    'ticket.stato_cambiato': 'Cambio di stato',
    'webhook.prova': 'Evento di prova',
};
/**
 * Attese prima di ciascun tentativo, in secondi, dallo schema di Standard Webhooks (decisione PO
 * del 03/10/2026): subito, 5 s, 5 min, 30 min, 2 h, 5 h, 10 h, 10 h. Otto tentativi in circa 27
 * ore e mezza; esauriti, l'evento è `fallito` e il consumer riconcilia con la lettura (#73).
 */
exports.CONSUMER_WEBHOOK_RETRY_DELAYS_SECONDS = [0, 5, 300, 1800, 7200, 18000, 36000, 36000];
/** Tentativi al massimo di un cambio di stato; l'evento di prova ne ha uno solo. */
exports.CONSUMER_WEBHOOK_MAX_ATTEMPTS = exports.CONSUMER_WEBHOOK_RETRY_DELAYS_SECONDS.length;
/** Attesa massima della risposta del consumer per tentativo: oltre, il tentativo è fallito (`timeout`). */
exports.CONSUMER_WEBHOOK_TIMEOUT_SECONDS = 10;
/** Ore di validità del segreto precedente dopo una rigenerazione (decisione PO del 03/10/2026). */
exports.CONSUMER_WEBHOOK_PREVIOUS_SECRET_HOURS = 24;
/** Consegne mostrate nel registro, dalla più recente. */
exports.CONSUMER_WEBHOOK_DELIVERIES_LIMIT = 50;
/** Prefisso del segreto di firma, come in Standard Webhooks: `whsec_` + 32 byte in base64. */
exports.CONSUMER_WEBHOOK_SECRET_PREFIX = 'whsec_';
/** Lunghezza massima dell'indirizzo della notifica. */
exports.CONSUMER_WEBHOOK_URL_MAX = 2048;
/** Stato di consegna di un evento. */
exports.CONSUMER_WEBHOOK_DELIVERY_STATUSES = ['in_coda', 'consegnato', 'fallito', 'scartato'];
exports.CONSUMER_WEBHOOK_DELIVERY_STATUS_LABELS = {
    in_coda: 'In coda',
    consegnato: 'Consegnato',
    fallito: 'Fallito',
    scartato: 'Scartato',
};
/**
 * Motivo dell'ultimo tentativo non riuscito; il codice HTTP, se c'è, è a parte. Mai il testo della
 * risposta del consumer: non si conserva.
 */
exports.CONSUMER_WEBHOOK_DELIVERY_ERRORS = [
    'risposta_non_2xx',
    'timeout',
    'rete',
    'dns',
    'indirizzo_non_ammesso',
    'segreto_illeggibile',
];
exports.CONSUMER_WEBHOOK_DELIVERY_ERROR_LABELS = {
    risposta_non_2xx: 'Risposta diversa da 2xx (i redirect non si seguono)',
    timeout: `Nessuna risposta entro ${exports.CONSUMER_WEBHOOK_TIMEOUT_SECONDS} secondi`,
    rete: 'Connessione non riuscita (rete o TLS)',
    dns: 'Nome dell’indirizzo non risolvibile, o DNS senza risposta',
    indirizzo_non_ammesso: 'L’indirizzo si risolve verso una rete privata, locale o di metadati',
    segreto_illeggibile: 'Segreto di firma non leggibile da questa istanza',
};
/** Perché un indirizzo è rifiutato (`details.reason` di `consumers.webhook_url_not_allowed`). */
exports.CONSUMER_WEBHOOK_URL_REJECTIONS = [
    'non_https',
    'porta_non_ammessa',
    'credenziali',
    'host_non_ammesso',
    'indirizzo_privato',
    'non_risolvibile',
];
exports.CONSUMER_WEBHOOK_URL_REJECTION_LABELS = {
    non_https: 'L’indirizzo deve iniziare con https://',
    porta_non_ammessa: 'Solo la porta standard di https (443): togli la porta dall’indirizzo',
    credenziali: 'L’indirizzo non può contenere nome utente o password',
    host_non_ammesso: 'L’host non è ammesso (localhost o nome locale)',
    indirizzo_privato: 'L’indirizzo porta a una rete privata, locale o di metadati del cloud',
    non_risolvibile: 'Il nome dell’host non si risolve',
};
// --- Evento inviato al consumer ----------------------------------------------------------------
const eventId = zod_1.z.uuid().meta({
    description: 'Identificativo dell’evento, uguale all’header `webhook-id`: resta lo stesso nei ritentativi, serve a scartare i duplicati',
});
/** Corpo dell'evento `ticket.stato_cambiato`: solo questi campi, mai dati della lavorazione. */
exports.consumerWebhookStateChangedEventSchema = zod_1.z
    .object({
    id: eventId,
    tipo: zod_1.z.literal('ticket.stato_cambiato'),
    istante: zod_1.z.iso.datetime().meta({
        description: 'Istante del cambio di stato, ISO 8601 in UTC: l’ordine di consegna non è garantito, il consumer tiene lo stato con l’istante più recente',
    }),
    ticket: zod_1.z.object({
        codice: zod_1.z.string().meta({ description: 'Codice del ticket (DEC-003)', example: 'EC-2026-0001' }),
        segnalante: zod_1.z.string().meta({
            description: 'Identificativo del segnalante, lo stesso inviato in `segnalante.id` alla creazione',
            example: 'op-123',
        }),
        statoPrecedente: zod_1.z.enum(ticket_state_js_1.TICKET_STATES).meta({ description: 'Stato prima del cambio (DEC-002)' }),
        stato: zod_1.z.enum(ticket_state_js_1.TICKET_STATES).meta({
            description: 'Stato dopo il cambio, uno dei sei di DEC-002; le etichette per chi ha segnalato sono del prodotto',
        }),
        categoria: zod_1.z.string().meta({ description: 'Categoria scelta alla segnalazione', example: 'Referti' }),
    }),
})
    .meta({ description: 'Cambio di stato di un ticket nell’ambito di lettura del consumer (DEC-013)' });
/** Corpo dell'evento di prova, inviato dall'amministratore dal registro dei consumer. */
exports.consumerWebhookTestEventSchema = zod_1.z
    .object({
    id: eventId,
    tipo: zod_1.z.literal('webhook.prova'),
    istante: zod_1.z.iso.datetime().meta({ description: 'Istante della richiesta di prova, ISO 8601 in UTC' }),
})
    .meta({ description: 'Evento di prova: nessun ticket, un solo tentativo' });
/** Un evento della notifica, per tipo. */
exports.consumerWebhookEventSchema = zod_1.z.discriminatedUnion('tipo', [
    exports.consumerWebhookStateChangedEventSchema,
    exports.consumerWebhookTestEventSchema,
]);
// --- Amministrazione nel registro dei consumer ---------------------------------------------------
/**
 * Indirizzo della notifica: URL assoluto senza spazi ai bordi. Qui solo la forma; `https`, assenza
 * di credenziali e reti ammesse li verifica l'API (anche dopo la risoluzione DNS), che risponde
 * `consumers.webhook_url_not_allowed` con il motivo.
 */
exports.consumerWebhookUrlSchema = zod_1.z
    .string({ error: 'Indica l’indirizzo della notifica' })
    .trim()
    .min(1, { error: 'Indica l’indirizzo della notifica' })
    .max(exports.CONSUMER_WEBHOOK_URL_MAX, { error: `Indirizzo troppo lungo (massimo ${exports.CONSUMER_WEBHOOK_URL_MAX} caratteri)` })
    .pipe(zod_1.z.url({ error: 'Indirizzo non valido: es. https://api.prodotto.it/easyhelp/eventi' }));
/** Corpo di `POST /consumers/:id/webhook` (attivazione) e `PATCH /consumers/:id/webhook` (nuovo indirizzo). */
exports.consumerWebhookUrlInputSchema = zod_1.z.object({ url: exports.consumerWebhookUrlSchema });
//# sourceMappingURL=consumer-webhooks.js.map